import React, { useEffect } from 'react';
import { PageLoading } from 'ui-toolkit';
import { useGetDetailedEscrowDataQuery } from 'API/order/escrow/EscrowBaseAPI';
import { useGetACHIncomingExpectedQuery } from 'API/accounting/AccountingApi';
import { ePermissions } from 'types/administration/userAdministration/ePermissions';
import { IncomingCheckSection } from './IncomingCheckSection';
import { sortByCreatedAt } from '../SortByCreatedAt';
import { IncomingWireSection } from './IncomingWireSection';
import { IncomingACHSection } from './IncomingACHSection';
import { MatchedDepositsSection } from './incomingMatches/MatchedDepositsSection';
import { eMatchedQueryValue } from 'types/accounting/eMatchedQueryValue';
import { PermissionWrapper } from '../../../accessControl/PermissionWrapper';

interface IIncomingFundsPage {
  orderId: string;
  escrowFileId: string;
}

export const IncomingFundsPage: React.FC<IIncomingFundsPage> = ({ orderId, escrowFileId }) => {
  const {
    isLoading: isLoadingIncomingData,
    data: incomingEscrowData,
    refetch,
  } = useGetDetailedEscrowDataQuery(orderId);
  const { isLoading: isLoadingGetIncomingACH, data: ACHIncomingExpected } = useGetACHIncomingExpectedQuery({
    fetchAll: true,
    escrowFileId,
  });

  const isLoading = isLoadingIncomingData || isLoadingGetIncomingACH;

  const sortedExpectedCheckDeposits = [...(incomingEscrowData?.checkDeposits || [])]
    .filter((cd) => cd.matched === eMatchedQueryValue.UNMATCHED)
    .sort(sortByCreatedAt);
  const sortedActualCheckDeposits = [...(incomingEscrowData?.checkDeposits || [])]
    .filter((cd) => cd.matched === eMatchedQueryValue.MATCHED || cd.matched === eMatchedQueryValue.MISMATCHED)
    .sort(sortByCreatedAt);
  const sortedExpectedWireDeposits = [...(incomingEscrowData?.wireDeposits || [])]
    .filter((cd) => cd.matched === eMatchedQueryValue.UNMATCHED)
    .sort(sortByCreatedAt);
  const sortedActualWireDeposits = [...(incomingEscrowData?.wireDeposits || [])]
    .filter((cd) => cd.matched === eMatchedQueryValue.MATCHED || cd.matched === eMatchedQueryValue.MISMATCHED)
    .sort(sortByCreatedAt);
  const sortedExpectedACHDeposits = [...(ACHIncomingExpected?.content || [])]
    .filter((ach) => !ach.matches.length)
    .sort(sortByCreatedAt);
  const sortedActualACHDeposits = [...(ACHIncomingExpected?.content || [])]
    .filter((ach) => ach.matches.length)
    .sort(sortByCreatedAt);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return (
    <PermissionWrapper
      allowedPermissions={[
        ePermissions.EscrowActualIncomingChecksRead,
        ePermissions.EscrowActualIncomingWiresRead,
        ePermissions.EscrowActualAchDepositsRead,
      ]}
    >
      <PageLoading isLoading={isLoading || !incomingEscrowData} />
      <IncomingACHSection achDeposits={sortedExpectedACHDeposits} orderId={orderId} escrowFileId={escrowFileId} />
      <br />
      <IncomingWireSection wireDeposits={sortedExpectedWireDeposits} orderId={orderId} escrowFileId={escrowFileId} />
      <br />
      <IncomingCheckSection checkDeposits={sortedExpectedCheckDeposits} escrowFileId={escrowFileId} orderId={orderId} />
      <br />
      <MatchedDepositsSection
        achDeposits={sortedActualACHDeposits}
        wireDeposits={sortedActualWireDeposits}
        checkDeposits={sortedActualCheckDeposits}
      />
    </PermissionWrapper>
  );
};
